import { medal } from '@alison/ui';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import ChevronRightOutlined from '@mui/icons-material/ChevronRightOutlined';
import ExpandLessOutlined from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha, lighten } from '@mui/material/styles';
import { type MouseEvent, type ReactNode, useEffect, useState } from 'react';
import { useLeadingKpiCoverage, useLeadingKpiOptions } from '../../lib/leadingKpi.hooks';
import { ControlButton } from './ControlButton';
import { FILTER_MENU_SLOT_PROPS, filterMenuItemSx } from './menuStyles';
import type { ScopeState } from './types';

export interface LeadingKpiOption {
  id: string;
  label: string;
  primary: boolean;
}

interface LeadingKpiControlProps {
  options: LeadingKpiOption[];
  value: string;
  onChange: (value: string) => void;
  accountIds: number[];
  scope: ScopeState;
  onScopeChange: (scope: ScopeState) => void;
  isLoading?: boolean;
}

interface ScopeLeadingKpiControlProps {
  scope: ScopeState;
  onChange: (scope: ScopeState) => void;
  accountIds: number[];
}

export function ScopeLeadingKpiControl({
  scope,
  onChange,
  accountIds,
}: ScopeLeadingKpiControlProps): ReactNode {
  const { data: leadingKpiOptions, isPending } = useLeadingKpiOptions(accountIds);

  useEffect(() => {
    const [topOption] = leadingKpiOptions ?? [];
    if (!topOption) return;
    if (leadingKpiOptions?.some((option) => option.id === scope.leadingKpi)) return;
    onChange({ ...scope, leadingKpi: topOption.id });
  }, [leadingKpiOptions, scope, onChange]);

  return (
    <LeadingKpiControl
      options={
        leadingKpiOptions ?? [
          { id: scope.leadingKpi, label: scope.leadingKpi.toUpperCase(), primary: true },
        ]
      }
      value={scope.leadingKpi}
      onChange={(leadingKpi) => onChange({ ...scope, leadingKpi })}
      accountIds={accountIds}
      scope={scope}
      onScopeChange={onChange}
      isLoading={isPending}
    />
  );
}

export function LeadingKpiControl({
  options,
  value,
  onChange,
  accountIds,
  scope,
  onScopeChange,
  isLoading = false,
}: LeadingKpiControlProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const current = options.find((option) => option.id === value);
  const { data: coverage } = useLeadingKpiCoverage(value, accountIds);

  const hasPrimaryOption = options.some((option) => option.primary);
  const showFullList = expanded || !hasPrimaryOption;
  const visibleOptions = showFullList
    ? [...options].sort((firstOption, secondOption) =>
        firstOption.label.localeCompare(secondOption.label, undefined, { sensitivity: 'base' }),
      )
    : options.filter((option) => option.primary || option.id === value);
  const showExpandButton = !showFullList && visibleOptions.length < options.length;
  const showCollapseButton = showFullList && hasPrimaryOption;

  const uncoveredPlatforms = coverage?.platforms.filter((platform) => !platform.covered) ?? [];
  const uncoveredBrands = coverage?.brands.filter((brand) => !brand.covered) ?? [];
  const hasWarning = uncoveredPlatforms.length > 0 || uncoveredBrands.length > 0;

  const coveredPlatforms = coverage?.platforms.filter((platform) => platform.covered) ?? [];
  const coveredBrands = coverage?.brands.filter((brand) => brand.covered) ?? [];
  const coveredPlatformIds = coveredPlatforms.map((platform) => platform.id);
  const coveredBrandIds = coveredBrands.map((brand) => brand.id);
  const namesOf = (entries: { label: string }[]): string =>
    entries.map((entry) => entry.label).join(', ');
  const uncoveredNames = namesOf([...uncoveredPlatforms, ...uncoveredBrands]);
  const hasNarrowerScope = coveredPlatformIds.length > 0 || coveredBrandIds.length > 0;

  function closeMenu(): void {
    setAnchor(null);
    setExpanded(false);
  }

  function narrowScope(): void {
    onScopeChange({
      ...scope,
      ...(uncoveredPlatforms.length > 0 ? { platform: coveredPlatformIds } : {}),
      ...(uncoveredBrands.length > 0 ? { brand: coveredBrandIds } : {}),
    });
    closeMenu();
  }

  return (
    <>
      <ControlButton
        label="Leading KPI"
        value={current?.label ?? value}
        open={anchor !== null}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        labelFirst
        warning={hasWarning}
      />
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={closeMenu}
        slotProps={{
          ...FILTER_MENU_SLOT_PROPS,
          paper: {
            ...FILTER_MENU_SLOT_PROPS.paper,
            sx: [
              FILTER_MENU_SLOT_PROPS.paper.sx,
              { maxHeight: showFullList ? '70vh' : 'none', pb: 0 },
            ],
          },
          list: { sx: { pb: 0 } },
        }}
      >
        <Box sx={{ width: 300, px: 1 }}>
          <Box
            component="span"
            sx={{
              display: 'block',
              p: 1,
              pt: 1,
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              color: 'text.disabled',
            }}
          >
            Leading KPI
          </Box>
          {hasWarning ? (
            <Box
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                mb: 1,
                p: 2,
                borderLeft: '3px solid',
                borderColor: lighten(theme.palette.warning.main, 0.4),
                borderRadius: 1,
                bgcolor: theme.palette.warning.bg,
              })}
            >
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <WarningAmberOutlined
                  sx={{
                    fontSize: 16,
                    color: medal.gold,
                  }}
                />
                <Box component="span" sx={{ fontSize: 11 }}>
                  {`This KPI isn't available for ${uncoveredNames}`}
                </Box>
              </Box>
              {hasNarrowerScope ? (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  endIcon={<ChevronRightOutlined fontSize="small" />}
                  onClick={narrowScope}
                  sx={(theme) => ({
                    borderColor: lighten(theme.palette.warning.main, 0.3),
                    color: medal.gold,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    fontWeight: 500,
                    justifyContent: 'space-between',
                    '&:hover': {
                      borderColor: lighten(theme.palette.warning.main, 0.3),
                      bgcolor: alpha(theme.palette.warning.main, 0.2),
                    },
                  })}
                >
                  {`Adjust scope to ${current?.label ?? value}'s availability`}
                </Button>
              ) : null}
            </Box>
          ) : null}
          {isLoading ? (
            <Box
              data-test="leading-kpi-loading"
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
            >
              <CircularProgress size={20} />
            </Box>
          ) : null}
          {isLoading
            ? null
            : visibleOptions.map((option) => (
                <MenuItem
                  key={option.id}
                  selected={option.id === value}
                  onClick={() => {
                    onChange(option.id);
                    closeMenu();
                  }}
                  sx={filterMenuItemSx}
                >
                  <ListItemText primary={option.label} sx={{ m: 0 }} />
                  {option.id === value ? <CheckOutlined fontSize="small" /> : null}
                </MenuItem>
              ))}
          {!isLoading && (showExpandButton || showCollapseButton) ? (
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
                mt: 1,
                pt: 2,
                pb: 1,
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                fullWidth
                size="small"
                variant="outlined"
                data-test={showExpandButton ? 'leading-kpi-show-more' : 'leading-kpi-show-less'}
                onClick={() => setExpanded(showExpandButton)}
                endIcon={
                  showExpandButton ? (
                    <ExpandMoreOutlined fontSize="small" />
                  ) : (
                    <ExpandLessOutlined fontSize="small" />
                  )
                }
              >
                {showExpandButton ? 'Show more' : 'Show less'}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Menu>
    </>
  );
}
